<template>
  <transition name="router-anim">
    <div class="inventory-dashboard-container">
      <!-- Compact Top Header Card -->
      <div class="dashboard-header-card compact">
        <!-- ROW 1: Title (Left) & Location Pills (Right) -->
        <div class="header-top-row flex-between align-center flex-wrap gap-10">
          <div class="header-title-wrap">
            <h2 class="dash-title">Inventory Dashboard</h2>
          </div>

          <!-- Location Selector Pills -->
          <div class="location-pill-group">
            <button
              v-for="loc in locations"
              :key="loc.id"
              class="location-pill-btn"
              :class="{ active: String(selectedLocation) === String(loc.id) }"
              @click="setLocationFilter(loc.id)"
            >
              🏢 {{ loc.location }}
            </button>
            <button
              class="location-pill-btn"
              :class="{ active: !selectedLocation }"
              @click="setLocationFilter('')"
            >
              📍 All Locations
            </button>
          </div>
        </div>

        <!-- ROW 2: Left: Total SKUs -> AirTrap -> GOT MOLD | Right: Stock Status Filters -->
        <div
          class="header-middle-row flex-between align-center flex-wrap gap-12"
        >
          <!-- Left: Brand KPI Summary Cards -->
          <div class="brand-cards-row flex-align-center gap-10">
            <div
              class="kpi-card-mini total-skus"
              :class="{ active: activeTab === 'consolidated' }"
              @click="switchTab('consolidated')"
              title="Click to view Consolidated Inventory (Both Brands)"
            >
              <span class="kpi-icon">🌐</span>
              <span class="kpi-mini-label">Consolidated:</span>
              <strong class="kpi-mini-val">{{ allMetrics.totalSkus }}</strong>
            </div>

            <div
              class="kpi-card-mini airtrap-card"
              :class="{ active: activeTab === 'airtrap' }"
              @click="switchTab('airtrap')"
              title="Click to view AirTrap Inventory"
            >
              <span class="kpi-icon">📦</span>
              <span class="kpi-mini-label">AirTrap:</span>
              <strong class="kpi-mini-val blue">{{
                airtrapMetrics.totalSkus
              }}</strong>
            </div>

            <div
              class="kpi-card-mini gotmold-card"
              :class="{ active: activeTab === 'gotmold' }"
              @click="switchTab('gotmold')"
              title="Click to view GOT MOLD Inventory"
            >
              <span class="kpi-icon">🧪</span>
              <span class="kpi-mini-label">GOT MOLD:</span>
              <strong class="kpi-mini-val green">{{
                gotmoldMetrics.totalSkus
              }}</strong>
            </div>
          </div>

          <!-- Right: Stock Status Filter Cards -->
          <div class="status-pills-row flex-align-center gap-10">
            <button
              class="back-all-stock-btn"
              v-if="selectedStockStatus !== 'all'"
              @click="setStockFilter('all')"
              title="Back to All Stock Statuses"
            >
              ↩️ <span class="back-text">All Stock</span>
            </button>

            <div
              class="status-filter-pill status-card-in"
              :class="{ active: selectedStockStatus === 'in' }"
              @click="setStockFilter('in')"
              title="Filter In Stock (> 20)"
            >
              <span class="status-dot green"></span>
              <span class="status-pill-label">In Stock (>20):</span>
              <strong class="status-pill-val green">{{
                activeBrandStockMetrics.inStockCount
              }}</strong>
            </div>

            <div
              class="status-filter-pill status-card-low"
              :class="{ active: selectedStockStatus === 'low' }"
              @click="setStockFilter('low')"
              title="Filter Low Stock (1 - 20)"
            >
              <span class="status-dot yellow"></span>
              <span class="status-pill-label">Low Stock (1-20):</span>
              <strong class="status-pill-val yellow">{{
                activeBrandStockMetrics.lowStockCount
              }}</strong>
            </div>

            <div
              class="status-filter-pill status-card-out"
              :class="{ active: selectedStockStatus === 'out' }"
              @click="setStockFilter('out')"
              title="Filter Out of Stock (0)"
            >
              <span class="status-dot red"></span>
              <span class="status-pill-label">Out of Stock (0):</span>
              <strong class="status-pill-val red">{{
                activeBrandStockMetrics.outOfStockCount
              }}</strong>
            </div>
          </div>
        </div>

        <!-- ROW 3: Search SKU & Reset Row -->
        <div class="header-search-row">
          <div class="search-input-wrap compact-search flex-grow">
            <input
              type="text"
              class="custom-input compact-input"
              placeholder="Search SKU, Description, Location..."
              v-model="searchQuery"
              @input="onFilterChange"
            />
            <button
              class="clear-search-btn"
              v-if="searchQuery"
              @click="clearSearch"
            >
              ✕
            </button>
          </div>

          <button
            class="reset-filter-btn mini-reset"
            @click="resetFilters"
            title="Reset all filters"
          >
            🔄 Reset
          </button>
        </div>
      </div>

      <!-- CONSOLIDATED SIDE-BY-SIDE TABLES (AirTrap Left, GOT MOLD Right) -->
      <div
        class="side-by-side-container mt-26"
        v-if="activeTab === 'consolidated'"
      >
        <!-- AirTrap Table Card (Left) -->
        <div class="table-card brand-section airtrap-section">
          <div
            class="table-card-header airtrap-header flex-between align-center"
          >
            <h3 class="table-title">
              <span class="brand-icon">📦</span> AirTrap Inventory
              <span class="badge-count blue"
                >{{ airtrapDisplayedItems.length }} SKUs</span
              >
            </h3>
          </div>

          <div class="table-responsive">
            <table class="modern-table">
              <thead>
                <tr>
                  <th @click="sortedBy('location')" class="sortable">
                    Loc
                    <span class="sort-icon">{{ getSortIcon("location") }}</span>
                  </th>
                  <th @click="sortedBy('item_description')" class="sortable">
                    Item Description
                    <span class="sort-icon">{{
                      getSortIcon("item_description")
                    }}</span>
                  </th>
                  <th @click="sortedBy('sku')" class="sortable">
                    SKU <span class="sort-icon">{{ getSortIcon("sku") }}</span>
                  </th>
                  <th
                    @click="sortedBy('available_inventory')"
                    class="sortable text-right"
                  >
                    Remaining Invy
                    <span class="sort-icon">{{
                      getSortIcon("available_inventory")
                    }}</span>
                  </th>
                </tr>
              </thead>
              <tbody v-if="airtrapDisplayedItems.length > 0">
                <tr
                  v-for="(item, idx) in airtrapDisplayedItems"
                  :key="
                    'at-' +
                      (item.sku || '') +
                      '-' +
                      (item.location_id || '') +
                      '-' +
                      idx
                  "
                  class="table-row"
                >
                  <td>
                    <span class="location-tag">{{
                      item.location || "N/A"
                    }}</span>
                  </td>
                  <td class="font-medium">
                    {{ item.item_description || item.sku }}
                  </td>
                  <td class="sku-cell">
                    <span
                      class="sku-link-wrap"
                      @click="viewSkuDetail(item.sku, item.item_description)"
                      title="View SKU Details"
                    >
                      <code class="clickable-sku-code">{{ item.sku }}</code>
                    </span>
                  </td>
                  <td
                    class="text-right font-bold remaining-cell"
                    :style="{ color: getStockColor(item.available_inventory) }"
                  >
                    <span>{{ item.available_inventory || 0 }}</span>
                    <span class="action-icon-group">
                      <button
                        class="view-icon-btn"
                        @click="viewSkuDetail(item.sku, item.item_description)"
                        title="View SKU Details"
                      >
                        👁️
                      </button>
                      <button
                        class="edit-icon-btn"
                        @click="edit(item)"
                        title="Edit Inventory"
                      >
                        ✏️
                      </button>
                    </span>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="4" class="text-center py-20 text-muted">
                    No AirTrap items matching criteria.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- GOT MOLD Table Card (Right) -->
        <div class="table-card brand-section gotmold-section">
          <div
            class="table-card-header gotmold-header flex-between align-center"
          >
            <h3 class="table-title">
              <span class="brand-icon">🧪</span> GOT MOLD Inventory
              <span class="badge-count green"
                >{{ gotmoldDisplayedItems.length }} SKUs</span
              >
            </h3>
          </div>

          <div class="table-responsive">
            <table class="modern-table">
              <thead>
                <tr>
                  <th @click="sortedBy('location')" class="sortable">
                    Loc
                    <span class="sort-icon">{{ getSortIcon("location") }}</span>
                  </th>
                  <th @click="sortedBy('item_description')" class="sortable">
                    Item Description
                    <span class="sort-icon">{{
                      getSortIcon("item_description")
                    }}</span>
                  </th>
                  <th @click="sortedBy('sku')" class="sortable">
                    SKU <span class="sort-icon">{{ getSortIcon("sku") }}</span>
                  </th>
                  <th
                    @click="sortedBy('available_inventory')"
                    class="sortable text-right"
                  >
                    Remaining Invy
                    <span class="sort-icon">{{
                      getSortIcon("available_inventory")
                    }}</span>
                  </th>
                </tr>
              </thead>
              <tbody v-if="gotmoldDisplayedItems.length > 0">
                <tr
                  v-for="(item, idx) in gotmoldDisplayedItems"
                  :key="
                    'gm-' +
                      (item.sku || '') +
                      '-' +
                      (item.location_id || '') +
                      '-' +
                      idx
                  "
                  class="table-row"
                >
                  <td>
                    <span class="location-tag">{{
                      item.location || "N/A"
                    }}</span>
                  </td>
                  <td class="font-medium">
                    {{ item.item_description || item.sku }}
                  </td>
                  <td class="sku-cell">
                    <span
                      class="sku-link-wrap"
                      @click="viewSkuDetail(item.sku, item.item_description)"
                      title="View SKU Details"
                    >
                      <code class="clickable-sku-code">{{ item.sku }}</code>
                    </span>
                  </td>
                  <td
                    class="text-right font-bold remaining-cell"
                    :style="{ color: getStockColor(item.available_inventory) }"
                  >
                    <span>{{ item.available_inventory || 0 }}</span>
                    <span class="action-icon-group">
                      <button
                        class="view-icon-btn"
                        @click="viewSkuDetail(item.sku, item.item_description)"
                        title="View SKU Details"
                      >
                        👁️
                      </button>
                      <button
                        class="edit-icon-btn"
                        @click="edit(item)"
                        title="Edit Inventory"
                      >
                        ✏️
                      </button>
                    </span>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="4" class="text-center py-20 text-muted">
                    No GOT MOLD items matching criteria.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- STANDALONE SINGLE BRAND VIEW (AirTrap Tab OR GOT MOLD Tab) -->
      <div class="table-card mt-26" v-if="activeTab !== 'consolidated'">
        <div
          class="table-card-header flex-between align-center"
          :class="activeTab === 'airtrap' ? 'airtrap-header' : 'gotmold-header'"
        >
          <h3 class="table-title">
            <span class="brand-icon">{{
              activeTab === "airtrap" ? "📦" : "🧪"
            }}</span>
            {{
              activeTab === "airtrap"
                ? "AirTrap Inventory Items"
                : "GOT MOLD Inventory Items"
            }}
            <span
              class="badge-count"
              :class="activeTab === 'airtrap' ? 'blue' : 'green'"
              >{{ displayedItems.length }} SKUs</span
            >
          </h3>
        </div>

        <div class="table-responsive">
          <table class="modern-table">
            <thead>
              <tr>
                <th @click="sortedBy('location')" class="sortable">
                  Loc
                  <span class="sort-icon">{{ getSortIcon("location") }}</span>
                </th>
                <th @click="sortedBy('item_description')" class="sortable">
                  Item Description
                  <span class="sort-icon">{{
                    getSortIcon("item_description")
                  }}</span>
                </th>
                <th @click="sortedBy('sku')" class="sortable">
                  SKU <span class="sort-icon">{{ getSortIcon("sku") }}</span>
                </th>
                <th
                  @click="sortedBy('units_per_price')"
                  class="sortable text-right"
                >
                  Unit Price
                  <span class="sort-icon">{{
                    getSortIcon("units_per_price")
                  }}</span>
                </th>
                <th
                  @click="sortedBy('available_inventory')"
                  class="sortable text-right"
                >
                  Remaining Invy
                  <span class="sort-icon">{{
                    getSortIcon("available_inventory")
                  }}</span>
                </th>
                <th
                  @click="sortedBy('total_price')"
                  class="sortable text-right"
                >
                  Total Value
                  <span class="sort-icon">{{
                    getSortIcon("total_price")
                  }}</span>
                </th>
                <th class="text-center">Status</th>
                <th class="text-center">Action</th>
              </tr>
            </thead>

            <tbody v-if="displayedItems.length > 0">
              <tr
                v-for="(item, idx) in displayedItems"
                :key="
                  'st-' +
                    (item.sku || '') +
                    '-' +
                    (item.location_id || '') +
                    '-' +
                    idx
                "
                class="table-row"
              >
                <td>
                  <span class="location-tag">{{ item.location || "N/A" }}</span>
                </td>
                <td class="font-medium">
                  {{ item.item_description || item.sku }}
                </td>
                <td class="sku-cell">
                  <span
                    class="sku-link-wrap"
                    @click="viewSkuDetail(item.sku, item.item_description)"
                    title="View SKU Details"
                  >
                    <code class="clickable-sku-code">{{ item.sku }}</code>
                  </span>
                </td>
                <td class="text-right">
                  ${{ formatUnitPrice(item.units_per_price) }}
                </td>
                <td class="text-right font-bold qty-num">
                  {{ item.available_inventory || 0 }}
                </td>
                <td class="text-right font-medium">
                  ${{ calculateItemTotalValue(item).toFixed(2) }}
                </td>
                <td class="text-center">
                  <span
                    class="status-pill"
                    :class="getStockStatusClass(item.available_inventory)"
                  >
                    {{ getStockStatusLabel(item.available_inventory) }}
                  </span>
                </td>
                <td class="text-center action-btns-cell">
                  <span class="action-icon-group">
                    <button
                      class="view-icon-btn"
                      @click="viewSkuDetail(item.sku, item.item_description)"
                      title="View SKU Details"
                    >
                      👁️
                    </button>
                    <button
                      class="edit-icon-btn"
                      @click="edit(item)"
                      title="Edit Inventory & Price"
                    >
                      ✏️
                    </button>
                  </span>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="8" class="no-data-cell text-center py-20">
                  <div class="empty-state">
                    <span class="empty-icon">🔍</span>
                    <p class="empty-text">
                      No inventory items matched your selected filters.
                    </p>
                    <button class="btn-secondary" @click="resetFilters">
                      Clear Filters
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "InventoryItems",
  data() {
    return {
      fullInventoryRaw: [],
      inventoryRaw: [],
      activeTab: "consolidated", // 'consolidated', 'airtrap', 'gotmold'
      selectedBrand: "ALL", // 'ALL', 'AirTrap', 'GOT MOLD'
      selectedLocation: "",
      selectedStockStatus: "all", // 'all', 'in', 'low', 'out'
      searchQuery: "",
      sortKey: "sku",
      sortDirection: "asc",
      locations: [],
      fillocation: null,
      colors: {
        blue: "#005EB8",
        green: "#43B02A",
        yellow: "#F1B434",
        red: "#E10E49",
        gray: "#6c757d"
      }
    };
  },
  methods: {
    ...mapActions([
      "setInventory",
      "setLocations",
      "setItems",
      "setFillocation"
    ]),

    viewSkuDetail(sku, itemDescription) {
      if (!sku) return;
      const query = {};
      if (itemDescription) {
        query.description = itemDescription;
      }
      this.$router.push({ name: "SkuDetail", params: { sku }, query });
    },

    switchTab(tab) {
      this.activeTab = tab;
      if (tab === "consolidated") {
        this.selectedBrand = "ALL";
      } else if (tab === "airtrap") {
        this.selectedBrand = "AirTrap";
      } else if (tab === "gotmold") {
        this.selectedBrand = "GOT MOLD";
      }
      this.applyLocalFilters();
    },

    onBrandChange() {
      if (this.selectedBrand === "ALL") {
        this.activeTab = "consolidated";
      } else if (this.selectedBrand === "AirTrap") {
        this.activeTab = "airtrap";
      } else if (this.selectedBrand === "GOT MOLD") {
        this.activeTab = "gotmold";
      }
      this.applyLocalFilters();
    },

    setStockFilter(status) {
      this.selectedStockStatus = status;
      this.applyLocalFilters();
    },

    setLocationFilter(locId) {
      this.selectedLocation = locId;
      this.applyLocalFilters();
    },

    onFilterChange() {
      this.applyLocalFilters();
    },

    clearSearch() {
      this.searchQuery = "";
      this.applyLocalFilters();
    },

    resetFilters() {
      this.selectedBrand = "ALL";
      this.activeTab = "consolidated";
      this.selectedLocation = "";
      this.selectedStockStatus = "all";
      this.searchQuery = "";
      this.sortKey = "sku";
      this.sortDirection = "asc";
      this.applyLocalFilters();
    },

    fetchData() {
      const params = {
        brand: "ALL",
        stock_status: "all"
      };
      this.setInventory(params)
        .then(res => {
          const data = Array.isArray(res)
            ? res
            : Array.isArray(this.$store.getters.getInventory)
            ? this.$store.getters.getInventory
            : [];
          this.fullInventoryRaw = this.mergeOwnerIds(data);
          this.applyLocalFilters();
        })
        .catch(err => {
          console.error("Error fetching inventory:", err);
        });
    },

    applyLocalFilters() {
      let list = [...this.fullInventoryRaw];

      // Filter by location if selected
      if (this.selectedLocation) {
        list = list.filter(item => {
          const locId =
            item.location_id != null
              ? item.location_id
              : item.loc_id != null
              ? item.loc_id
              : item.location;
          return String(locId) === String(this.selectedLocation);
        });
      }

      // Filter by search query if typed
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(item => {
          const sku = String(item.sku || "").toLowerCase();
          const desc = String(item.item_description || "").toLowerCase();
          const loc = String(item.location || "").toLowerCase();
          return sku.includes(q) || desc.includes(q) || loc.includes(q);
        });
      }

      // Filter by stock status if selected
      if (this.selectedStockStatus && this.selectedStockStatus !== "all") {
        const status = this.selectedStockStatus;
        list = list.filter(item => {
          const qty = parseInt(item.available_inventory || 0);
          if (status === "in") return qty > 20;
          if (status === "low") return qty >= 1 && qty <= 20;
          if (status === "out") return qty === 0;
          return true;
        });
      }

      this.inventoryRaw = list;
    },

    edit(item) {
      this.$emit("editAddInv", {
        show: true,
        item: item
      });
    },

    sortedBy(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortDirection = "asc";
      }
    },

    getSortIcon(key) {
      if (this.sortKey !== key) return "↕️";
      return this.sortDirection === "asc" ? "⬆️" : "⬇️";
    },

    formatUnitPrice(val) {
      if (
        val === null ||
        val === undefined ||
        val === "" ||
        isNaN(parseFloat(val))
      ) {
        return "0.00";
      }
      return parseFloat(val).toFixed(2);
    },

    calculateItemTotalValue(item) {
      const price = parseFloat(this.formatUnitPrice(item.units_per_price));
      const qty = parseFloat(item.available_inventory || 0);
      return parseFloat((price * qty).toFixed(2));
    },

    getBrandName(ownerId) {
      if (!ownerId) return "GOT MOLD";
      const str = String(ownerId)
        .trim()
        .toLowerCase();
      if (str.includes("airtrap")) return "AirTrap";
      return "GOT MOLD";
    },

    getBrandClass(ownerId) {
      const brand = this.getBrandName(ownerId);
      return brand === "AirTrap" ? "airtrap-badge" : "gotmold-badge";
    },

    getStockColor(qty) {
      const num = parseInt(qty || 0);
      if (num === 0) return this.colors.red;
      if (num <= 20) return this.colors.yellow;
      return this.colors.green;
    },

    getStockStatusLabel(qty) {
      const num = parseInt(qty || 0);
      if (num === 0) return "Out of Stock";
      if (num <= 20) return "Low Stock";
      return "In Stock";
    },

    getStockStatusClass(qty) {
      const num = parseInt(qty || 0);
      if (num === 0) return "status-out";
      if (num <= 20) return "status-low";
      return "status-in";
    },

    normalizeSku(value) {
      if (value === null || value === undefined) return "";
      return String(value);
    },

    buildOwnerIdMap(items) {
      const map = new Map();
      if (!Array.isArray(items)) return map;
      for (const it of items) {
        const sku = this.normalizeSku(
          it &&
            (it.sku != null ? it.sku : it.SKU != null ? it.SKU : it.seller_sku)
        );
        if (!sku) continue;
        const ownerId =
          it &&
          (it.owner_id != null
            ? it.owner_id
            : it.ownerId != null
            ? it.ownerId
            : it.owner != null
            ? it.owner
            : it.ownerID);
        if (ownerId === null || ownerId === undefined) continue;
        map.set(sku, ownerId);
      }
      return map;
    },

    mergeOwnerIds(inventory) {
      const items = this.$store.getters.getItem;
      const ownerIdMap = this.buildOwnerIdMap(items);
      const rows = Array.isArray(inventory) ? inventory : [];
      return rows.map(row => {
        const next = { ...row };
        const sku = this.normalizeSku(next.sku);
        next.sku = sku;
        const ownerId = ownerIdMap.get(sku);
        next.owner_id =
          ownerId != null
            ? ownerId
            : next.owner_id != null
            ? next.owner_id
            : next.ownerId != null
            ? next.ownerId
            : next.owner != null
            ? next.owner
            : "GOTMOLD";
        return next;
      });
    },

    computeMetricsForItems(itemsList) {
      const list = Array.isArray(itemsList) ? itemsList : [];
      const totalSkus = list.length;
      let totalAvailable = 0;
      let lowStockCount = 0;
      let totalValue = 0;

      for (const item of list) {
        const avail = parseInt(item.available_inventory || 0);
        totalAvailable += avail;
        if (avail <= 20) {
          lowStockCount++;
        }
        totalValue += this.calculateItemTotalValue(item);
      }

      return {
        totalSkus,
        totalAvailable,
        lowStockCount,
        totalValue
      };
    },

    sortItems(itemsList) {
      if (!this.sortKey) return [...itemsList];
      const key = this.sortKey;
      const dir = this.sortDirection;
      return [...itemsList].sort((a, b) => {
        let aVal = a[key];
        let bVal = b[key];
        if (
          key === "units_per_price" ||
          key === "available_inventory" ||
          key === "reserved" ||
          key === "total_price"
        ) {
          aVal = parseFloat(aVal || 0);
          bVal = parseFloat(bVal || 0);
        } else {
          aVal = String(aVal || "").toLowerCase();
          bVal = String(bVal || "").toLowerCase();
        }
        if (aVal < bVal) return dir === "asc" ? -1 : 1;
        if (aVal > bVal) return dir === "asc" ? 1 : -1;
        return 0;
      });
    },

    selectDefaultWestLocation() {
      if (Array.isArray(this.locations) && this.locations.length > 0) {
        const westLoc = this.locations.find(l =>
          String(l.location || "")
            .toLowerCase()
            .includes("west")
        );
        if (westLoc) {
          this.selectedLocation = westLoc.id;
        }
      }
    }
  },

  computed: {
    ...mapGetters(["getInventory", "getLocation", "getFillocation"]),

    airtrapItems() {
      return this.inventoryRaw.filter(
        item => this.getBrandName(item.owner_id) === "AirTrap"
      );
    },

    gotmoldItems() {
      return this.inventoryRaw.filter(
        item => this.getBrandName(item.owner_id) === "GOT MOLD"
      );
    },

    displayedItems() {
      let list = [];
      if (this.activeTab === "airtrap" || this.selectedBrand === "AirTrap") {
        list = this.airtrapItems;
      } else if (
        this.activeTab === "gotmold" ||
        this.selectedBrand === "GOT MOLD"
      ) {
        list = this.gotmoldItems;
      } else {
        list = this.inventoryRaw;
      }
      return this.sortItems(list);
    },

    airtrapDisplayedItems() {
      return this.sortItems(this.airtrapItems);
    },

    gotmoldDisplayedItems() {
      return this.sortItems(this.gotmoldItems);
    },

    selectedLocationName() {
      if (!this.selectedLocation) return "All Locations";
      const found = this.locations.find(
        l => String(l.id) === String(this.selectedLocation)
      );
      return found ? found.location : "Selected Location";
    },

    // LEVEL 1: Primary Location Filtered Dataset
    locationFilteredItems() {
      if (!this.selectedLocation) {
        return this.fullInventoryRaw;
      }
      return this.fullInventoryRaw.filter(item => {
        const locId =
          item.location_id != null
            ? item.location_id
            : item.loc_id != null
            ? item.loc_id
            : item.location;
        return String(locId) === String(this.selectedLocation);
      });
    },

    // LEVEL 2: Brand SKU items evaluated for selected Location
    locationAirtrapItems() {
      return this.locationFilteredItems.filter(
        item => this.getBrandName(item.owner_id) === "AirTrap"
      );
    },

    locationGotmoldItems() {
      return this.locationFilteredItems.filter(
        item => this.getBrandName(item.owner_id) === "GOT MOLD"
      );
    },

    allMetrics() {
      return this.computeMetricsForItems(this.locationFilteredItems);
    },

    currentMetrics() {
      return this.computeMetricsForItems(this.displayedItems);
    },

    airtrapMetrics() {
      return this.computeMetricsForItems(this.locationAirtrapItems);
    },

    gotmoldMetrics() {
      return this.computeMetricsForItems(this.locationGotmoldItems);
    },

    // LEVEL 3: Stock Status metrics evaluated for Location + Brand
    activeBrandStockMetrics() {
      let list = this.locationFilteredItems;
      if (this.selectedBrand === "AirTrap" || this.activeTab === "airtrap") {
        list = this.locationAirtrapItems;
      } else if (
        this.selectedBrand === "GOT MOLD" ||
        this.activeTab === "gotmold"
      ) {
        list = this.locationGotmoldItems;
      }

      let totalCount = list.length;
      let inStockCount = 0;
      let lowStockCount = 0;
      let outOfStockCount = 0;

      for (const item of list) {
        const qty = parseInt(item.available_inventory || 0);
        if (qty > 20) {
          inStockCount++;
        } else if (qty >= 1) {
          lowStockCount++;
        } else {
          outOfStockCount++;
        }
      }

      return {
        totalCount,
        inStockCount,
        lowStockCount,
        outOfStockCount
      };
    }
  },

  watch: {
    getInventory(newVal) {
      if (Array.isArray(newVal)) {
        this.inventoryRaw = this.mergeOwnerIds(newVal);
      }
    },
    getLocation(locs) {
      this.locations = locs || [];
      if (!this.selectedLocation) {
        this.selectDefaultWestLocation();
        this.applyLocalFilters();
      }
    }
  },

  mounted() {
    this.locations = this.$store.getters.getLocation || [];
    this.fillocation = this.$store.getters.getFillocation;
    this.selectDefaultWestLocation();
    this.fetchData();
  }
};
</script>

<style scoped>
.inventory-dashboard-container {
  padding: 10px 0 30px 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  color: #2c3e50;
}

.dashboard-header-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid #eef2f5;
}

.dashboard-header-card.compact {
  padding: 18px 22px;
  margin-bottom: 20px;
}

.dash-title {
  font-size: 22px;
  font-weight: 700;
  color: #005eb8;
  margin: 0;
  letter-spacing: -0.2px;
}

.header-top-row {
  margin-bottom: 16px;
}

.header-middle-row {
  margin-top: 16px;
}

.header-search-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.flex-align-center {
  display: flex;
  align-items: center;
}

.summary-search-group {
  display: flex;
  align-items: center;
}

.search-reset-row {
  display: flex;
  align-items: center;
  max-width: 440px;
}

.compact-search {
  max-width: 100%;
}

.compact-input {
  padding: 8px 14px;
  font-size: 13px;
  border-radius: 6px;
  height: 38px;
}

.mini-reset {
  padding: 8px 16px;
  font-size: 13px;
  height: 38px;
  white-space: nowrap;
}

/* Compact Inline Brand Cards */
.brand-cards-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.kpi-card-mini {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 16px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
}

.kpi-card-mini:hover {
  border-color: #005eb8;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 94, 184, 0.1);
}

.kpi-card-mini.active {
  border-color: #005eb8;
  background: #eef6ff;
  box-shadow: 0 2px 8px rgba(0, 94, 184, 0.2);
}

.kpi-card-mini.airtrap-card.active {
  border-color: #005eb8;
  background: #eef6ff;
}

.kpi-card-mini.gotmold-card.active {
  border-color: #43b02a;
  background: #ecfdf5;
}

.kpi-mini-label {
  font-weight: 600;
  color: #475569;
}

.kpi-mini-val {
  font-weight: 700;
  color: #0f172a;
}

.kpi-mini-val.blue {
  color: #005eb8;
}
.kpi-mini-val.green {
  color: #15803d;
}

/* Inline Stock Status Filter Pills */
.status-pills-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.status-filter-pill {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
}

.status-filter-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.status-filter-pill.active {
  border-color: #0f172a;
  background: #f8fafc;
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.15);
}

.status-filter-pill.status-card-in.active {
  border-color: #15803d;
  background: #f0fdf4;
}

.status-filter-pill.status-card-low.active {
  border-color: #b45309;
  background: #fffbeb;
}

.status-filter-pill.status-card-out.active {
  border-color: #b91c1c;
  background: #fef2f2;
}

.status-pill-label {
  font-weight: 600;
  color: #64748b;
}

.status-pill-val {
  font-weight: 700;
}

.status-pill-val.green {
  color: #15803d;
}
.status-pill-val.yellow {
  color: #b45309;
}
.status-pill-val.red {
  color: #b91c1c;
}

/* Compact Back to All Stock Arrow Button */
.status-cards-flex {
  display: flex;
  align-items: center;
  gap: 10px;
}

.back-all-stock-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.back-all-stock-btn:hover {
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.2);
}

.back-all-stock-btn .back-text {
  font-size: 12px;
}

/* Location Hierarchy Pill Group */
.location-pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.location-pill-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 20px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.location-pill-btn:hover {
  background: #eef6ff;
  border-color: #005eb8;
  color: #005eb8;
}

.location-pill-btn.active {
  background: #005eb8;
  border-color: #005eb8;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 94, 184, 0.25);
}

.tab-switcher {
  display: flex;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 10px;
  gap: 4px;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn:hover {
  color: #005eb8;
  background: rgba(255, 255, 255, 0.6);
}

.tab-btn.active {
  background: #ffffff;
  color: #005eb8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.tab-btn.airtrap-tab.active {
  color: #005eb8;
}

.tab-btn.gotmold-tab.active {
  color: #43b02a;
}

/* Sequential Filter Hierarchy Header Elements */
.hierarchy-step-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hierarchy-step-badge {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 8px;
  border-radius: 6px;
  background: #334155;
  color: #ffffff;
}

.hierarchy-step-badge.blue {
  background: #005eb8;
}
.hierarchy-step-badge.green {
  background: #16a34a;
}
.hierarchy-step-badge.dark {
  background: #0f172a;
}

.hierarchy-step-title {
  font-size: 13px;
  color: #334155;
}

/* Location Hierarchy Pill Group */
.location-hierarchy-bar {
  background: #f8fafc;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.location-pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.location-pill-btn {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.location-pill-btn:hover {
  background: #eef6ff;
  border-color: #005eb8;
  color: #005eb8;
}

.location-pill-btn.active {
  background: #005eb8 !important;
  border-color: #005eb8 !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(0, 94, 184, 0.25) !important;
}

/* KPI Cards Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 2px solid #e2e8f0;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  border-color: #cbd5e1;
}

.kpi-card.active {
  border-color: #005eb8;
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(0, 94, 184, 0.15);
}

.kpi-card.airtrap-card {
  border-left: 4px solid #005eb8;
  background: #f0f7ff;
}

.kpi-card.airtrap-card.active {
  border-color: #005eb8;
  background: #eef6ff;
  box-shadow: 0 4px 14px rgba(0, 94, 184, 0.2);
}

.kpi-card.gotmold-card {
  border-left: 4px solid #43b02a;
  background: #f0fdf4;
}

.kpi-card.gotmold-card.active {
  border-color: #43b02a;
  background: #ecfdf5;
  box-shadow: 0 4px 14px rgba(67, 176, 42, 0.2);
}

.kpi-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.kpi-info {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kpi-value {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 2px 0 0 0;
}

.kpi-sub {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  margin-left: 4px;
}

.kpi-active-badge {
  position: absolute;
  top: 10px;
  right: 12px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #334155;
  color: #ffffff;
}

.kpi-active-badge.blue {
  background: #005eb8;
}

.kpi-active-badge.green {
  background: #43b02a;
}

/* Stock Status Interactive Filter Cards */
.stock-status-bar {
  padding-top: 14px;
  border-top: 1px solid #f1f5f9;
}

.status-bar-title {
  font-size: 13px;
  color: #334155;
}

.status-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.status-filter-card {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.status-filter-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  border-color: #94a3b8;
}

.status-filter-card.active {
  border-color: #005eb8;
  box-shadow: 0 0 0 3px rgba(0, 94, 184, 0.15);
  background: #f8fafc;
}

.status-filter-card.status-card-in.active {
  border-color: #15803d;
  box-shadow: 0 0 0 3px rgba(21, 128, 61, 0.15);
  background: #f0fdf4;
}

.status-filter-card.status-card-low.active {
  border-color: #b45309;
  box-shadow: 0 0 0 3px rgba(180, 83, 9, 0.15);
  background: #fffbeb;
}

.status-filter-card.status-card-out.active {
  border-color: #b91c1c;
  box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.15);
  background: #fef2f2;
}

.status-filter-header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-dot.grey {
  background: #64748b;
}
.status-dot.green {
  background: #15803d;
}
.status-dot.yellow {
  background: #b45309;
}
.status-dot.red {
  background: #b91c1c;
}

.status-filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-filter-count {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 4px 0 0 0;
}

.status-filter-count.green {
  color: #15803d;
}
.status-filter-count.yellow {
  color: #b45309;
}
.status-filter-count.red {
  color: #b91c1c;
}

/* Filter Toolbar */
.filter-toolbar-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  border: 1px solid #eef2f5;
}

.toolbar-flex {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-group.flex-grow {
  flex-grow: 1;
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.custom-select,
.custom-input {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background-color: #ffffff;
  outline: none;
  transition: border-color 0.2s;
}

.custom-select:focus,
.custom-input:focus {
  border-color: #005eb8;
  box-shadow: 0 0 0 3px rgba(0, 94, 184, 0.1);
}

.brand-select {
  font-weight: 600;
  color: #005eb8;
  border-color: #005eb8;
}

.search-input-wrap {
  position: relative;
  width: 100%;
}

.search-input-wrap .custom-input {
  width: 100%;
  padding-right: 32px;
}

.clear-search-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  font-size: 14px;
}

.reset-filter-btn {
  height: 40px;
  padding: 0 16px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #475569;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.reset-filter-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
}

/* Side-by-Side Tables Grid */
.side-by-side-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: start;
}

@media (max-width: 992px) {
  .side-by-side-container {
    grid-template-columns: 1fr;
  }
}

/* Table Cards */
.table-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid #eef2f5;
  overflow: hidden;
}

.table-card-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
}

.airtrap-header {
  background: #f0f7ff;
  border-bottom-color: #e0eeff;
}

.gotmold-header {
  background: #f0fdf4;
  border-bottom-color: #dcfce7;
}

.table-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.badge-count {
  font-size: 11px;
  padding: 2px 8px;
  background: #e2e8f0;
  color: #334155;
  border-radius: 10px;
  font-weight: 600;
}

.badge-count.blue {
  background: #dbeafe;
  color: #1e40af;
}

.badge-count.green {
  background: #dcfce7;
  color: #15803d;
}

.section-quick-stats {
  font-size: 12px;
  color: #475569;
}

/* Modern Table */
.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 13px;
}

.modern-table thead tr {
  background: #005eb8;
  color: #ffffff;
}

.modern-table th {
  padding: 10px 14px;
  font-weight: 600;
  white-space: nowrap;
}

.modern-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.modern-table th.sortable:hover {
  background: #004c97;
}

.sort-icon {
  font-size: 11px;
  margin-left: 4px;
  opacity: 0.8;
}

.modern-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.table-row:hover {
  background-color: #f8fafc;
}

.location-tag {
  background: #f1f5f9;
  color: #334155;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 12px;
}

/* Stock Status Pill */
.status-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.status-pill.mini {
  padding: 2px 7px;
  font-size: 10px;
  border-radius: 10px;
}

.status-pill.status-in {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.status-pill.status-low {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fde68a;
}

.status-pill.status-out {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fca5a5;
}

.qty-num {
  color: #0f172a;
}

.remaining-cell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.action-icon-group {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.view-icon-btn,
.edit-icon-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 3px 5px;
  border-radius: 4px;
  transition: all 0.2s ease;
  line-height: 1;
  opacity: 0.85;
}

.view-icon-btn:hover {
  background: #e0f2fe;
  opacity: 1;
  transform: scale(1.15);
}

.edit-icon-btn:hover {
  background: #fef3c7;
  opacity: 1;
  transform: scale(1.15);
}

/* Empty State */
.empty-state {
  padding: 30px 15px;
}

.empty-icon {
  font-size: 32px;
}

.empty-text {
  font-size: 13px;
  color: #64748b;
  margin: 6px 0 12px 0;
}

.btn-secondary {
  padding: 6px 14px;
  background: #005eb8;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
}

/* Utility Helpers */
.flex-between {
  display: flex;
  justify-content: space-between;
}
.flex-wrap {
  flex-wrap: wrap;
}
.align-center {
  align-items: center;
}
.gap-15 {
  gap: 15px;
}
.mt-15 {
  margin-top: 15px;
}
.mt-20 {
  margin-top: 20px;
}
.py-20 {
  padding-top: 20px;
  padding-bottom: 20px;
}
.text-right {
  text-align: right;
}
.text-center {
  text-align: center;
}
.font-medium {
  font-weight: 500;
}
.font-bold {
  font-weight: 700;
}
.text-muted {
  color: #94a3b8;
}

/* SKU Link & Eye Icon Styles */
.sku-link-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.sku-link-wrap:hover code {
  color: #005eb8;
  text-decoration: underline;
}

.action-btns-cell {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.action-view-btn {
  background: #e6f0fa;
  color: #005eb8;
  border: 1px solid #b8d5f5;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-view-btn:hover {
  background: #005eb8;
  color: #ffffff;
}
</style>
